import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

type SimpleTableProps<T> = {
  className?: string;
  data: Array<T>;
  isLoading?: boolean;
  onRowClick?: (row: T) => void;
  columns: {
    key: string;
    label: string;
    width?: string;
    render: (row: T, index: number) => React.ReactNode;
  }[];
};

function SimpleTable<T>({
  className,
  data,
  isLoading,
  onRowClick,
  columns,
}: SimpleTableProps<T>) {
  const { t } = useTranslation();

  const renderNoData = () => {
    return (
      <tr>
        <td className="my-table-cell" colSpan={columns.length}>
          <div className="flex items-center justify-center">{t("no-data")}</div>
        </td>
      </tr>
    );
  };

  const renderLoadingData = () => {
    return (
      <tr>
        <td className="my-table-cell" colSpan={columns.length}>
          <div className="flex items-center justify-center">
            {t("loading data...")}
          </div>
        </td>
      </tr>
    );
  };

  const renderTableRow = (row: T, index: number) => {
    return (
      <tr
        key={index}
        className={cn("hover:bg-accent", onRowClick && "cursor-pointer")}
        onClick={() => {
          if (!onRowClick) return;
          onRowClick(row);
        }}
      >
        {columns.map((column) => (
          <td key={column.key} className="my-table-cell">
            {column.render(row, index)}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div className={cn(className, "rounded-xl border bg-white shadow")}>
      <table className="table w-full divide-y">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                style={{ width: column.width }}
                className="my-table-cell w-auto font-bold"
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading
            ? renderLoadingData()
            : data.length === 0
              ? renderNoData()
              : data.map(renderTableRow)}
        </tbody>
      </table>
    </div>
  );
}

export default SimpleTable;
